import { Container, Loader, Stack } from 'component-library';
import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { withSuspenseBoundary } from '@/components';
import { useOrganization } from '@/hooks/useOrganization';
import { SignedInPageLayout } from '@/layout/signed-in-page';
import { CreateLimitedAccessOrganization } from '@/pages/shared/components/plot-group/CreateLimitedAccessOrganization';
import { CreatePlotGroup } from '@/pages/shared/components/plot-group/CreatePlotGroup';
import { useSetOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { PlotListDesktop } from '@/pages/shared/plots/components/PlotListDesktop';
import { useDefaultCustomCells } from '@/pages/shared/plots/hooks/useGetCustomCells';
import { ProjectsMap } from '@/pages/shared/projects/components/projectsMap/ProjectsMap';
import { paths } from '@/routing';
import { isNullish } from '@/utils/booleans';
import { buildPath } from '@/utils/buildPath';

const PlotsList = withSuspenseBoundary(
  () => {
    const { organizationId } = useParams();

    const customColumns = useDefaultCustomCells();
    return (
      <>
        <PlotListDesktop
          organizationIdArg={organizationId}
          customColumns={customColumns}
          enableRowSelection={true}
          showSort={false}
        />
      </>
    );
  },
  <Loader className='min-h-[300px]' />,
);

export const OrganizationPage = () => {
  const { organizationId } = useParams();
  const organization = useOrganization({ id: organizationId }).data.results[0];

  const setOrganizationId = useSetOrganizationId();
  useEffect(() => {
    if (!isNullish(organizationId)) {
      setOrganizationId(organizationId);
    }
  }, [organizationId, setOrganizationId]);

  const [searchParams] = useSearchParams();
  const plotGroupId = searchParams.get('plotGroupId');

  if (isNullish(organization)) throw new Error(`Unknown organization with id ${organizationId}`);

  return (
    <SignedInPageLayout
      items={[]}
      withCustomContentContainer
      navBarProps={{
        title: organization.name,
        backPath: buildPath(paths.staff.organizations),
        showFilters: true,
      }}
    >
      <Container gutterWidth={{ sm: 4 }} className='gap-10'>
        {isNullish(plotGroupId) && !isNullish(organization.id) && (
          <Stack direction='row' className='justify-end gap-4'>
            <CreatePlotGroup organizationId={organization.id} />
            <CreateLimitedAccessOrganization organizationId={organization.id} />
          </Stack>
        )}
        <div data-testid='org-map' className='h-[60vh] animate-in fade-in'>
          <ProjectsMap organizationIdArg={organization.id} />
        </div>
        <PlotsList data-testid='org-plot-list' />
      </Container>
    </SignedInPageLayout>
  );
};
